import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Percent } from "lucide-react";
import { cn, formatEur } from "@/lib/utils";
import { getMargenBruto, saveMargenConfig } from "@/lib/api";
import { codigoVisible, esCuentaDeAsiento, SimboloAsiento } from "@/lib/useModoAsiento";

// Margen Bruto configurable (petición Héctor sept-2026): ingresos (70/71)
// − costes (60/61) sobre las cuentas seleccionadas A MÁXIMO DÍGITO. Por
// defecto entran todas; desmarcar excluye la cuenta del cálculo en TODA la
// empresa (la selección se guarda por empresa y aplica a todos los años).
// El cálculo vive en backend (domain/margen.py) — la misma cifra alimenta
// el ratio "Margen Bruto" de Comparativa Ratios.

const GRUPOS = [
  { id: "70", titulo: "Ventas (70)", ingreso: true },
  { id: "71", titulo: "Variación de existencias PT (71)", ingreso: true },
  { id: "60", titulo: "Compras (60)", ingreso: false },
  { id: "61", titulo: "Variación de existencias (61)", ingreso: false },
];

export default function MargenBrutoContent({ ejercicioId, empresaId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const res = await getMargenBruto(ejercicioId);
      setData(res.data);
    } catch {
      toast.error("Error al cargar el margen bruto");
    } finally {
      setLoading(false);
    }
  }, [ejercicioId]);

  useEffect(() => {
    setLoading(true);
    cargar();
  }, [cargar]);

  const porGrupo = useMemo(() => {
    const out = { 60: [], 61: [], 70: [], 71: [] };
    (data?.detalle || []).forEach((c) => {
      if (out[c.grupo]) out[c.grupo].push(c);
    });
    return out;
  }, [data]);

  const toggleCuenta = async (codigo) => {
    if (!data) return;
    const excluidas = new Set(data.excluidas || []);
    if (excluidas.has(codigo)) excluidas.delete(codigo);
    else excluidas.add(codigo);
    setGuardando(true);
    try {
      await saveMargenConfig(empresaId, [...excluidas]);
      await cargar();
      toast.success("Selección del margen bruto guardada (aplica a toda la empresa)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar la selección");
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!data) {
    return <p className="text-muted-foreground py-8 text-center">Sin datos.</p>;
  }

  const fmt = formatEur;
  const kpis = [
    { label: "Ingresos incluidos (70/71)", valor: fmt(data.ingresos) },
    { label: "Costes incluidos (60/61)", valor: fmt(data.costes) },
    { label: "Margen Bruto", valor: fmt(data.margen_bruto), destacado: true },
    {
      label: "% s/ ingresos",
      valor: data.margen_bruto_pct != null ? `${String(data.margen_bruto_pct).replace(".", ",")} %` : "—",
      destacado: true,
    },
  ];

  return (
    <div className="space-y-4" data-testid="margen-bruto-content">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <Card key={k.label} className={cn(k.destacado && "border-primary/40")}>
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground">{k.label}</p>
              <p className={cn("text-lg font-semibold font-mono tabular-nums",
                k.destacado && "text-primary")}>{k.valor}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Percent className="h-4 w-4" />
            Cuentas del margen (selección a máximo dígito)
          </CardTitle>
          <p className="text-[11px] text-muted-foreground">
            Margen Bruto = ingresos (70/71) − costes (60/61) de las cuentas marcadas.
            Por defecto entran todas; desmarcar una la excluye del cálculo en <b>toda la
            empresa</b> (todos los años y el ratio de Comparativa Ratios). Los saldos
            mostrados son los de este ejercicio.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {GRUPOS.map((g) => {
              const cuentas = porGrupo[g.id] || [];
              const incluidas = cuentas.filter((c) => c.incluida).length;
              return (
                <div key={g.id} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium">{g.titulo}</span>
                    <Badge variant="outline" className="text-[11px]">
                      {incluidas}/{cuentas.length} incluidas
                    </Badge>
                    <Badge variant="secondary" className="text-[11px]">
                      {g.ingreso ? "ingreso" : "coste"}
                    </Badge>
                  </div>
                  <div className="border rounded-md max-h-56 overflow-y-auto divide-y">
                    {cuentas.length === 0 ? (
                      <p className="text-[11px] text-muted-foreground px-2 py-2">
                        Sin cuentas del grupo {g.id} en este ejercicio
                      </p>
                    ) : (
                      cuentas.map((c) => (
                        <label
                          key={c.codigo_full}
                          className={cn(
                            "flex items-center gap-2 px-2 py-1 text-[12px] cursor-pointer hover:bg-muted/30",
                            !c.incluida && "opacity-60 bg-muted/20"
                          )}
                        >
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-primary shrink-0"
                            checked={c.incluida}
                            disabled={guardando}
                            onChange={() => toggleCuenta(c.codigo_full)}
                            data-testid={`margen-cta-${c.codigo_full}`}
                          />
                          <span className="font-mono shrink-0">
                            {codigoVisible(c.codigo_full)}
                            {esCuentaDeAsiento(c.codigo_full) && <SimboloAsiento className="ml-0.5" />}
                          </span>
                          <span className="flex-1 truncate" title={c.nombre}>{c.nombre}</span>
                          <span className="font-mono tabular-nums text-muted-foreground shrink-0">
                            {fmt(c.saldo)}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
